import { useCallback, useRef } from 'react';

import { gql } from '@apollo/client';
import type { ApolloCache, ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { Apollo } from 'src/gql/generated';
import { agentThreadTypename } from 'src/helpers';
import { RECENT_CHAT_THREADS_VARIABLES } from 'src/hooks/ai-chat';
import { v4 as uuid4 } from 'uuid';

import { $chatThreadState } from '../chat-state';
import type { PinnedSkill } from '../ChatPanel/ChatEditor/ChatEditorSkill/utils';

const AGENT_MESSAGE_FRAGMENT = gql`
  fragment AgentMessageFields on AgentMessage {
    id
    __typename
    role
    status
    text
    data
    feedback
    planState
    createdAt
  }
`;

type AgentThreadMessage = Apollo.GetAgentThreadQuery['agentThread']['messages'][number];

export function useSendUserMessage({
  editorDocumentId,
  onNavigateToThread,
  threadId: currentThreadId,
  visibleContext,
}: {
  editorDocumentId: UUID | null;
  onNavigateToThread: (threadId: UUID) => void;
  threadId: UUID | null;
  visibleContext?: Apollo.AgentVisibleContextInput;
}) {
  const apolloClient = useApolloClient();
  const [sendAgentPrompt] = Apollo.useSendAgentPromptMutation({ errorPolicy: 'none' });
  const sendingRef = useRef(false);

  /**
   * Sends a prompt to the agent.
   * Sets the focus to the bottom input after sending the prompt.
   * @param text - The prompt text to send
   * @param skills - Immutable Skill versions pinned to this message in composer order
   */
  return useCallback(
    async (
      text: string,
      skills: PinnedSkill[] = [],
      onConfirmed?: (confirmedThreadId?: string) => void
    ): Promise<boolean> => {
      if (sendingRef.current) return false;
      const trimmedText = text.trim();
      if (!trimmedText.length && !skills.length) return false;

      const userMessageId = uuid4();
      const assistantMessageId = uuid4();
      const isNewThread = !currentThreadId;
      const threadId = currentThreadId ?? uuid4();

      sendingRef.current = true;
      let confirmed = false;
      try {
        const createdAt = new Date().toISOString();
        writeOptimisticMessages({
          apolloClient,
          createdAt,
          isNewThread,
          messages: [
            buildUserMessage({ createdAt, id: userMessageId, text: trimmedText }),
            buildStreamingAssistantMessage({ createdAt, id: assistantMessageId }),
          ],
          threadId,
        });
        $chatThreadState.thinkingMessage = {
          messageId: assistantMessageId,
          text: 'Analyzing request...',
        };

        const variables = {
          input: {
            agentSkills: skills.map(skill => ({
              skillId: skill.skillId,
              versionId: skill.versionId,
            })),
            assistantMessageId,
            editorDocumentId,
            prompt: trimmedText,
            threadId,
            userMessageId,
            ...(visibleContext ? { visibleContext } : {}),
          },
        };
        const { data } = await sendAgentPrompt({ variables });
        if (!data) throw new Error('The server did not confirm the message.');
        confirmed = true;
        // Sync the submitted draft before navigation can replace its editor.
        onConfirmed?.(threadId);
        if (isNewThread) onNavigateToThread(threadId);
      } catch (error) {
        // A confirmed send stays successful even if local cache or navigation fails.
        Sentry.captureException(error, {
          extra: { assistantMessageId, threadId },
          tags: { component: 'chat-harness-send-prompt' },
        });
        if (confirmed) return true;
        $chatThreadState.thinkingMessage = null;
        markMessageErrored(apolloClient.cache, assistantMessageId);
        return false;
      } finally {
        sendingRef.current = false;
      }

      setTimeout(() => {
        const userMessageElement = document.getElementById(userMessageId);
        if (userMessageElement) userMessageElement.scrollIntoView({ behavior: 'smooth' });
      }, 0);
      return true;
    },
    [
      apolloClient,
      currentThreadId,
      editorDocumentId,
      onNavigateToThread,
      sendAgentPrompt,
      visibleContext,
    ]
  );
}

function buildUserMessage({
  createdAt,
  id,
  text,
}: {
  createdAt: string;
  id: UUID;
  text: string;
}): AgentThreadMessage {
  return {
    __typename: 'AgentMessage',
    createdAt,
    data: {},
    feedback: null,
    id,
    planState: null,
    role: Apollo.AgentMessageRole.User,
    status: Apollo.AgentMessageStatus.Complete,
    text,
  };
}

function buildStreamingAssistantMessage({
  createdAt,
  id,
}: {
  createdAt: string;
  id: UUID;
}): AgentThreadMessage {
  return {
    __typename: 'AgentMessage',
    createdAt,
    data: { suggestionStates: {} },
    feedback: null,
    id,
    planState: null,
    role: Apollo.AgentMessageRole.Assistant,
    status: Apollo.AgentMessageStatus.Streaming,
    text: '',
  };
}

function writeOptimisticMessages({
  apolloClient,
  createdAt,
  isNewThread,
  messages,
  threadId,
}: {
  apolloClient: ApolloClient;
  createdAt: string;
  isNewThread: boolean;
  messages: [userMessage: AgentThreadMessage, assistantMessage: AgentThreadMessage];
  threadId: UUID;
}) {
  if (isNewThread) {
    apolloClient.cache.writeQuery<Apollo.GetAgentThreadQuery, Apollo.GetAgentThreadQueryVariables>({
      data: {
        __typename: 'Query',
        agentThread: {
          __typename: agentThreadTypename,
          artifacts: [],
          context: [],
          id: threadId,
          messages,
          title: 'Chat',
        },
      },
      query: Apollo.GetAgentThreadDocument,
      variables: { id: threadId },
    });

    const existingThreads =
      apolloClient.readQuery<Apollo.GetAgentThreadsQuery, Apollo.GetAgentThreadsQueryVariables>({
        query: Apollo.GetAgentThreadsDocument,
        variables: RECENT_CHAT_THREADS_VARIABLES,
      })?.agentThreads ?? [];
    apolloClient.writeQuery<Apollo.GetAgentThreadsQuery, Apollo.GetAgentThreadsQueryVariables>({
      data: {
        __typename: 'Query',
        agentThreads: [
          { __typename: agentThreadTypename, createdAt, id: threadId, title: 'New Chat' },
          ...existingThreads,
        ],
      },
      query: Apollo.GetAgentThreadsDocument,
      variables: RECENT_CHAT_THREADS_VARIABLES,
    });
    return;
  }

  const messageRefs = messages.map(message =>
    apolloClient.cache.writeFragment({ data: message, fragment: AGENT_MESSAGE_FRAGMENT })
  );
  apolloClient.cache.modify({
    fields: {
      messages: (existingMessages = []) => [...existingMessages, ...messageRefs],
    },
    id: apolloClient.cache.identify({ __typename: agentThreadTypename, id: threadId }),
  });
}

function markMessageErrored(cache: ApolloCache, messageId: UUID) {
  cache.modify({
    fields: { status: () => Apollo.AgentMessageStatus.Error },
    id: cache.identify({ __typename: 'AgentMessage', id: messageId }),
  });
}
